import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FavoriteState } from "./favorite.state";
import {
  toggleFavorite,
  getFavorites,
  getFavoriteIds,
  getFavoriteTabs,
  bulkDeleteFavorites,
} from "./favorite.thunk";

const initialState: FavoriteState = {
  data: null,
  status: "idle",
  error: null,
  favoritedProducts: [],
  togglingProducts: [],
  tabs: [],
  tabsStatus: "idle",
  idsStatus: "idle",
  bulkDeleteStatus: "idle",
};

export const favoriteSlice = createSlice({
  name: "favorite",
  initialState,
  reducers: {
    setFavoritedProducts: (state, action: PayloadAction<number[]>) => {
      state.favoritedProducts = action.payload;
    },
    addFavoritedProduct: (state, action: PayloadAction<number>) => {
      if (!state.favoritedProducts.includes(action.payload)) {
        state.favoritedProducts.push(action.payload);
      }
    },
    removeFavoritedProduct: (state, action: PayloadAction<number>) => {
      state.favoritedProducts = state.favoritedProducts.filter(
        (id) => id !== action.payload,
      );
    },
    initializeFavoriteState: (
      state,
      action: PayloadAction<{ productId: number; isFavorited: boolean }>,
    ) => {
      const { productId, isFavorited } = action.payload;
      if (isFavorited && !state.favoritedProducts.includes(productId)) {
        state.favoritedProducts.push(productId);
      }
    },
    clearFavorites: (state) => {
      state.data = null;
      state.favoritedProducts = [];
      state.togglingProducts = [];
      state.tabs = [];
      state.status = "idle";
      state.tabsStatus = "idle";
      state.idsStatus = "idle";
      state.bulkDeleteStatus = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(toggleFavorite.pending, (state, action) => {
        const { productId } = action.meta.arg;
        if (!state.togglingProducts.includes(productId)) {
          state.togglingProducts.push(productId);
        }
        state.error = null;
      })
      .addCase(toggleFavorite.fulfilled, (state, action) => {
        const { productId } = action.meta.arg;
        state.togglingProducts = state.togglingProducts.filter(
          (id) => id !== productId,
        );
        if (action.payload.data.is_favorited) {
          if (!state.favoritedProducts.includes(productId)) {
            state.favoritedProducts.push(productId);
          }
        } else {
          state.favoritedProducts = state.favoritedProducts.filter(
            (id) => id !== productId,
          );
        }
        state.status = "success";
      })
      .addCase(toggleFavorite.rejected, (state, action) => {
        const { productId } = action.meta.arg;
        state.togglingProducts = state.togglingProducts.filter(
          (id) => id !== productId,
        );
        state.status = "error";
        state.error =
          action.payload ?? "Favori durumu değiştirilirken bir hata oluştu.";
      })

      .addCase(getFavorites.pending, (state, action) => {
        const isFirstPage = !action.meta.arg || action.meta.arg.page === 1;
        if (isFirstPage && state.data) {
          state.data.items = [];
        }
        state.status = "loading";
        state.error = null;
      })
      .addCase(getFavorites.fulfilled, (state, action) => {
        state.status = "success";
        const isFirstPage = action.payload.data.pagination.current_page === 1;
        if (isFirstPage || !state.data) {
          state.data = action.payload.data;
        } else {
          state.data = {
            ...state.data,
            items: [...state.data.items, ...action.payload.data.items],
            pagination: action.payload.data.pagination,
          };
        }
      })
      .addCase(getFavorites.rejected, (state, action) => {
        state.status = "error";
        state.error =
          action.payload ?? "Favoriler yüklenirken bir hata oluştu.";
      })

      .addCase(getFavoriteIds.pending, (state) => {
        state.idsStatus = "loading";
        state.error = null;
      })
      .addCase(getFavoriteIds.fulfilled, (state, action) => {
        state.idsStatus = "success";
        state.favoritedProducts = action.payload.data.product_ids;
      })
      .addCase(getFavoriteIds.rejected, (state, action) => {
        state.idsStatus = "error";
        state.error =
          action.payload ?? "Favori ID'leri yüklenirken bir hata oluştu.";
      })

      .addCase(getFavoriteTabs.pending, (state) => {
        state.tabsStatus = "loading";
        state.error = null;
      })
      .addCase(getFavoriteTabs.fulfilled, (state, action) => {
        state.tabsStatus = "success";
        state.tabs = action.payload.data;
      })
      .addCase(getFavoriteTabs.rejected, (state, action) => {
        state.tabsStatus = "error";
        state.error = action.payload ?? "Tablar yüklenirken bir hata oluştu.";
      })

      .addCase(bulkDeleteFavorites.pending, (state) => {
        state.bulkDeleteStatus = "loading";
        state.error = null;
      })
      .addCase(bulkDeleteFavorites.fulfilled, (state, action) => {
        state.bulkDeleteStatus = "success";
        const deletedIds = action.meta.arg.product_ids;
        state.favoritedProducts = state.favoritedProducts.filter(
          (id) => !deletedIds.includes(id),
        );
      })
      .addCase(bulkDeleteFavorites.rejected, (state, action) => {
        state.bulkDeleteStatus = "error";
        state.error = action.payload ?? "Favoriler silinirken bir hata oluştu.";
      });
  },
});

export const {
  setFavoritedProducts,
  addFavoritedProduct,
  removeFavoritedProduct,
  clearFavorites,
  initializeFavoriteState,
} = favoriteSlice.actions;

export default favoriteSlice.reducer;
